import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { PlusIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { resetBulkState, resetUsersV3, UserV3 } from "../../../store/slices/UsersV3Slice";
import { fetchUserOrganizationAsync } from "../../../store/thunks/OrganizationsV3Thunk";
import {
  fetchAccessibleOrganizationsAsync,
  fetchUsersV3Async,
  fetchUsersV3StatsAsync,
} from "../../../store/thunks/UsersV3Thunk";
import { UserListFilters } from "../../../services/UsersV3Service";
import {
  canManageUsers,
  canViewUsers,
  isOrgScopedRole,
  USERS_PER_PAGE,
  USERS_ROLE_LABELS,
  UserSortKey,
} from "../../../constants/users";
import { canViewOrganizations } from "../../../constants/organizations";
import { Button } from "../../Catalyst/button";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import OrgRoleInviteDialog from "./invite/OrgRoleInviteDialog";
import { PaginationNext, PaginationPrevious } from "../../Catalyst/pagination";
import StatCard from "../../common/StatCard";
import SupportIcon from "../../../assets/SupportIcon";
import { formatStatValue } from "../Organizations/utils";
import GlobalUsersTable from "./GlobalUsersTable";
import UserFilters from "./UserFilters";
import UserSearchField from "./UserSearchField";
import SearchResultsSkeleton from "../GlobalSearch/components/SearchResultsSkeleton";
import EmptySearchIcon from "../../../assets/EmptySearchIcon";
import BulkActionBar from "./BulkActionBar";
import MoveUsersDialog from "./MoveUsersDialog";
import DeactivateUsersDialog from "./DeactivateUsersDialog";
import SupportDialog from "./SupportDialog";
import {
  EMPTY_SELECTION,
  getSelectedCount,
  getSingleSourceOrganization,
  nextSort,
  parseUserListQuery,
  RowSelection,
  setPageSelected,
  toApiSelection,
  toggleRow,
  toUserListSearchParams,
  UserSort,
} from "./utils";

// Matches the Search page's live search (ADM-2).
const SEARCH_DEBOUNCE_MS = 300;

const LoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="h-8 w-8 animate-spin text-zinc-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

interface GlobalUserListProps {
  userRole: string | null;
  // The signed-in user's own organization; names the page title.
  userOrganizationId: number | null;
}

// ADM-89: every user the signed-in admin can see, across all orgs, at /users.
// It sits alongside the original user list dashboard (/user-list-dashboard)
// until fully rolled out. Org-scoped
// lists live under the hierarchy at /organizations/:orgId/users (ADM-5).
const GlobalUserList = ({ userRole, userOrganizationId }: GlobalUserListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = canManageUsers(userRole);

  const {
    users,
    page,
    totalPages,
    totalItems,
    listStatus,
    listError,
    stats,
    organizationOptions,
    organizationOptionsStatus,
  } = useSelector((state: RootState) => state.UsersV3Store);
  const userOrganizationName = useSelector(
    (state: RootState) => state.OrganizationsV3Store.userOrganizationName
  );

  const [initialQuery] = useState(() => parseUserListQuery(searchParams));
  const [currentPage, setCurrentPage] = useState(initialQuery.page);
  const [filters, setFilters] = useState<UserListFilters>(initialQuery.filters);
  const [sort, setSort] = useState<UserSort>(initialQuery.sort);
  const [searchInput, setSearchInput] = useState(initialQuery.filters.search);
  const [selection, setSelection] = useState<RowSelection>(EMPTY_SELECTION);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  // Bumped on every open so each bulk dialog mounts with fresh state.
  const [bulkDialogKey, setBulkDialogKey] = useState(0);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  useEffect(() => {
    if (userOrganizationId != null && !userOrganizationName) {
      dispatch(fetchUserOrganizationAsync(userOrganizationId));
    }
  }, [userOrganizationId, userOrganizationName, dispatch]);

  const roleLabel = (userRole && USERS_ROLE_LABELS[userRole]) || "";
  // Org Admin counts cover their org and its sub-orgs only, so the card copy
  // must not claim "all orgs" for them.
  const statsScope = isOrgScopedRole(userRole) ? "across your organizations" : "across all orgs";
  const welcomeTitle = userOrganizationName
    ? `Welcome back, ${[userOrganizationName, roleLabel].filter(Boolean).join(" ")}`
    : "Welcome back";

  const fetchList = useCallback(() => {
    dispatch(fetchUsersV3Async({ page: currentPage, perPage: USERS_PER_PAGE, filters, sort }));
  }, [currentPage, filters, sort, dispatch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setSearchParams(toUserListSearchParams({ filters, sort, page: currentPage }), { replace: true });
  }, [filters, sort, currentPage, setSearchParams]);

  useEffect(() => {
    dispatch(fetchUsersV3StatsAsync());
    dispatch(fetchAccessibleOrganizationsAsync());
  }, [dispatch]);

  // If the current page no longer exists (e.g. a move emptied the last page of
  // an org-filtered list), step back to the last page that does.
  useEffect(() => {
    if (listStatus === STATUS.SUCCESS && users.length === 0 && currentPage > 1) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [listStatus, users.length, currentPage, totalPages]);

  useEffect(() => {
    return () => {
      dispatch(resetUsersV3());
    };
  }, [dispatch]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const applySearch = useCallback((text: string) => {
    const search = text.trim();
    if (search === filtersRef.current.search) return;
    setFilters({ ...filtersRef.current, search });
    setCurrentPage(1);
    setSelection(EMPTY_SELECTION);
  }, []);

  const debouncedApplySearch = useMemo(() => debounce(applySearch, SEARCH_DEBOUNCE_MS), [applySearch]);

  useEffect(() => {
    return () => debouncedApplySearch.cancel();
  }, [debouncedApplySearch]);

  // A filter or sort change redefines the result set: back to page 1, and drop
  // the selection so a bulk action never applies to rows the admin cannot see.
  const handleFiltersChange = (next: UserListFilters) => {
    debouncedApplySearch.cancel();
    setFilters({ ...next, search: searchInput.trim() });
    setCurrentPage(1);
    setSelection(EMPTY_SELECTION);
  };

  const handleSearchChange = (text: string) => {
    setSearchInput(text);
    debouncedApplySearch(text);
  };

  const handleSearchClear = () => {
    debouncedApplySearch.cancel();
    setSearchInput("");
    applySearch("");
  };

  const handleSort = (key: UserSortKey) => {
    setSort((current) => nextSort(current, key));
    setCurrentPage(1);
    setSelection(EMPTY_SELECTION);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setCurrentPage(nextPage);
  };

  const handleViewActivity = (user: UserV3) => {
    if (!user.organization) return;
    navigate(`/user-profile/${user.organization.id}/${user.id}`, {
      state: { backTo: `/users${location.search}`, backLabel: "Back to all users" },
    });
  };

  // The org hierarchy pages are not open to Org Admins yet, so their org names
  // render as plain text instead of a link to an "Access restricted" screen.
  const handleOpenOrganization = useMemo(
    () =>
      canViewOrganizations(userRole)
        ? (organizationId: number) => navigate(`/organizations/${organizationId}`)
        : null,
    [userRole, navigate]
  );

  const openBulkDialog = (open: (value: boolean) => void) => {
    dispatch(resetBulkState());
    setBulkDialogKey((key) => key + 1);
    open(true);
  };

  // Invited users appear in the list with an Invited status (ADM-92), so reload
  // the current page and the cards.
  const handleInvited = useCallback(() => {
    fetchList();
    dispatch(fetchUsersV3StatsAsync());
  }, [fetchList, dispatch]);

  const handleBulkCompleted = () => {
    setSelection(EMPTY_SELECTION);
    fetchList();
    dispatch(fetchUsersV3StatsAsync());
  };

  const selectedCount = getSelectedCount(selection, totalItems);
  const apiSelection = toApiSelection(selection, filters);
  const sourceOrganization = getSingleSourceOrganization(selection);

  const isLoading = listStatus === STATUS.LOADING || listStatus === STATUS.IDLE;
  const isFailed = listStatus === STATUS.FAILED;
  const isEmpty = listStatus === STATUS.SUCCESS && users.length === 0;
  const hasResults = listStatus === STATUS.SUCCESS && users.length > 0;
  const hasFilters =
    filters.role.length > 0 || filters.status.length > 0 || filters.organizationId != null;
  const isSearching = filters.search !== "";

  const renderTableBody = () => {
    if (isLoading) return isSearching ? <SearchResultsSkeleton /> : <LoadingState />;

    if (isFailed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          <p className="max-w-md text-center text-sm font-medium text-red-500">
            {listError || "Something went wrong while loading users. Please try again."}
          </p>
        </div>
      );
    }

    // Same empty state as the Search page.
    if (isEmpty && isSearching) {
      return (
        <div className="flex h-[195px] flex-col items-center justify-center gap-4">
          <EmptySearchIcon />
          <p className="w-[294px] text-center text-sm font-medium text-texthead opacity-50">
            There's nothing to show here. Maybe check the spelling or try another keywords
          </p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-20">
          <p className="text-sm font-medium text-texthead opacity-70">No users found</p>
          <p className="max-w-md text-center text-sm text-textColor">
            {hasFilters ? "There are no users matching these filters." : "There are no users yet."}
          </p>
        </div>
      );
    }

    return (
      <div className="px-6">
        <GlobalUsersTable
          users={users}
          sort={sort}
          onSort={handleSort}
          selectable={canManage}
          selection={selection}
          onToggleRow={(user) => setSelection((current) => toggleRow(current, user))}
          onTogglePage={(selected) => setSelection((current) => setPageSelected(current, users, selected))}
          onOpenOrganization={handleOpenOrganization}
          onViewActivity={handleViewActivity}
        />
      </div>
    );
  };

  return (
    // Bottom padding keeps the last row and pagination clear of the floating bulk bar.
    <div className={`p-8 ${canManage && selectedCount > 0 ? "pb-28" : ""}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="desktext10 min-w-0 truncate">{welcomeTitle}</h1>
        <div className="flex shrink-0 items-center gap-4">
          <UserSearchField
            className="w-60 sm:w-72 md:w-[360px] xl:w-[490px]"
            value={searchInput}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
          />
          <Button outline onClick={() => setIsSupportOpen(true)} aria-label="Open support form">
            <SupportIcon />
            Support
          </Button>
          {/* "+ Create" per the ADM-89 Figma; Coach is read-only and cannot invite. */}
          {canManage && (
            <Dropdown>
              <DropdownButton color="dark">
                <PlusIcon />
                Create
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={() => setIsInviteOpen(true)}>
                  <UserPlusIcon />
                  <DropdownLabel>Invite Users</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Active Users"
          description={`Total active users ${statsScope}`}
          value={formatStatValue(stats?.activeUsers)}
          valueLabel="active users"
        />
        <StatCard
          title="Signed Up Users"
          description="Signed up but not finished onboarding"
          value={formatStatValue(stats?.signedUpUsers)}
          valueLabel="signed up users"
        />
        <StatCard
          title="Pending Invites"
          description="Invited but haven't signed up yet"
          value={formatStatValue(stats?.invitedUsers)}
          valueLabel="invited users"
        />
        <StatCard
          title="Deactivated Users"
          description={`Total deactivated users ${statsScope}`}
          value={formatStatValue(stats?.deactivatedUsers)}
          valueLabel="deactivated users"
        />
      </div>

      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-4">
          <div className="desktext11 text-texthead">Users</div>
          <UserFilters
            filters={filters}
            onChange={handleFiltersChange}
            organizationOptions={organizationOptions}
            isOrganizationOptionsLoading={organizationOptionsStatus === STATUS.LOADING}
            isOrganizationOptionsError={organizationOptionsStatus === STATUS.FAILED}
          />
        </div>

        {renderTableBody()}

        {hasResults && (
          <div className="flex items-center justify-between border-t border-shadowborder px-6 py-3">
            <span className="text-sm font-medium text-textColor3">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-3">
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </div>
          </div>
        )}
      </div>

      {canManage && selectedCount > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          isAllPagesSelected={selection.mode === "allMatching"}
          onToggleAllPages={(selected) =>
            setSelection(
              selected
                ? { mode: "allMatching", excludedIds: [] }
                : setPageSelected(EMPTY_SELECTION, users, true)
            )
          }
          onMove={() => openBulkDialog(setIsMoveOpen)}
          onDeactivate={() => openBulkDialog(setIsDeactivateOpen)}
          onDismiss={() => setSelection(EMPTY_SELECTION)}
        />
      )}

      {/* Kept mounted after the selection clears so a result step can still show. */}
      {canManage && (
        <>
          <MoveUsersDialog
            key={`move-${bulkDialogKey}`}
            open={isMoveOpen}
            onClose={() => setIsMoveOpen(false)}
            selection={apiSelection}
            selectedCount={selectedCount}
            sourceOrganization={sourceOrganization}
            onCompleted={handleBulkCompleted}
          />
          <DeactivateUsersDialog
            key={`deactivate-${bulkDialogKey}`}
            open={isDeactivateOpen}
            onClose={() => setIsDeactivateOpen(false)}
            selection={apiSelection}
            selectedCount={selectedCount}
            onCompleted={handleBulkCompleted}
          />
        </>
      )}

      <SupportDialog open={isSupportOpen} onClose={() => setIsSupportOpen(false)} />

      {canManage && (
        <OrgRoleInviteDialog
          open={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          onInvited={handleInvited}
        />
      )}
    </div>
  );
};

const Users = () => {
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);

  // userAccount is fetched in AppLayout; treat its absence as "still loading".
  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!canViewUsers(userAccount.user_role)) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view users. Please contact an administrator if you
            believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <GlobalUserList
      userRole={userAccount.user_role}
      userOrganizationId={userAccount.organization_id ? Number(userAccount.organization_id) : null}
    />
  );
};

export default Users;
